import React, { useState, useEffect } from 'react';
import { useBasketStore } from '../state/basketStore';
import WheelPicker from './WheelPicker';
import BarcodeDisplay from './BarcodeDisplay';
import ImageCarousel from './ImageCarousel';
import { getOrgId } from '../utils/tenantBoot';

/**
 * Check if running in kiosk mode
 */
const isKioskMode = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('kiosk') === '1';
};

/**
 * Sticky Product Detail Panel - Multi-Attribute Selector
 * Shows product with multiple attributes (Flavour with images, Strength as text, etc.)
 * Layout: Description | Attribute Images (vertical) | Selected Image | Info & Attribute Selectors
 * LOCATION: Only displayed in kiosk mode (kiosk=1 URL parameter)
 */
export default function ProductDetailPanel({ product, onClose, catalogSettings = {} }) {
  const kioskMode = isKioskMode();
  
  // Debug logging for kiosk mode
  if (kioskMode) {
    console.log('[ProductDetailPanel] KIOSK MODE - Product data:', {
      name: product.product_name || product.name,
      barcode: product.barcode,
      bin_location: product.bin_location,
      hasLocation: !!product.bin_location
    });
  }
  const [quantity, setQuantity] = useState(1);
  const [imageError, setImageError] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showZoom, setShowZoom] = useState(false);
  const [useWheelPicker, setUseWheelPicker] = useState(true); // Always use wheel picker for all screens
  const [productImages, setProductImages] = useState([]); // Product-level images
  const [loadingImages, setLoadingImages] = useState(false);
  // The ONE authoritative selection for variant-group products: an index into
  // productImages. Everything else (title, barcode, price, stock, main image,
  // thumbnail highlight, wheel value, Add to Basket) is DERIVED from this
  // single value below - no other state is allowed to independently "select"
  // a variant, which is what previously caused the first-click race.
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentProductMedia, setCurrentProductMedia] = useState([]); // Direct product media (non-variant)
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0); // Track which product media is selected
  
  // Initialize collapsed state based on screen size
  const [descriptionCollapsed, setDescriptionCollapsed] = useState(() => {
    return window.innerWidth <= 1024; // Collapsed by default on small screens
  });
  
  const addItem = useBasketStore(state => state.addItem);
  
  // Helper function to fix relative image URLs (memoized to prevent re-renders)
  const fixImageUrl = React.useCallback((url) => {
    if (!url) return url;
    
    const baseUrl = import.meta.env.VITE_RCC_BASE || '';
    
    // Strip localhost URLs and make them relative (for network access)
    if (url.includes('127.0.0.1') || url.includes('localhost')) {
      const match = url.match(/(?:127\.0\.0\.1:\d+|localhost:\d+)(\/.*)/);
      if (match) {
        url = match[1]; // Extract just the path part
      }
    }
    
    // Prepend baseUrl if it's a relative path
    return url.startsWith('http') ? url : `${baseUrl}${url}`;
  }, []);
  
  // Track selection for EACH attribute separately: {Flavour: "Strawberry", Strength: "12mg"} -
  // used ONLY for non-variant products (old per-attribute image/price system).
  // Variant-group products derive their displayed selection from
  // selectedImageIndex (see variantSelections memo below) instead.
  const [selections, setSelections] = useState({});
  const [lastChangedAttribute, setLastChangedAttribute] = useState(null);
  
  // Refs for wheel picker scroll behavior
  const imageStripContainerRef = React.useRef(null);
  const thumbnailsContainerRef = React.useRef(null);

  // Full RCC product rows for every product in the org's catalogue (built once
  // below), keyed by product_id - the same data CatalogView/ShelfView already
  // use, so selecting a sibling variant can resolve its REAL identity instead
  // of the lightweight variant-group entry. State (not a ref) so derived
  // memos below correctly recompute once it finishes loading.
  const [productsCache, setProductsCache] = useState({});

  // Resolve the real identity for a variant-group entry (title, barcode,
  // price, stock, image_url). Falls back to the variant entry's OWN fields
  // (still real values, just less complete) if the full-catalogue cache
  // hasn't finished loading yet, rather than silently keeping whichever
  // product was previously shown.
  const resolveEffectiveProduct = React.useCallback((variantEntry) => {
    const fullRecord = productsCache[variantEntry.product_id];
    if (fullRecord) {
      return {
        ...fullRecord,
        variantData: product.variantData,
        qty_on_hand: variantEntry.qty_on_hand ?? fullRecord.qty_on_hand,
        promotion: variantEntry.promotion ?? fullRecord.promotion ?? null,
      };
    }
    return {
      ...product,
      product_id: variantEntry.product_id,
      product_name: variantEntry.product_name,
      name: variantEntry.product_name,
      barcode: variantEntry.barcode || product.barcode,
      price: variantEntry.price ?? product.price,
      qty_on_hand: variantEntry.qty_on_hand ?? product.qty_on_hand,
      promotion: variantEntry.promotion ?? product.promotion ?? null,
      variantData: product.variantData,
    };
  }, [product, productsCache]);
  
  // Fetch product-level images
  useEffect(() => {
    const fetchProductImages = async () => {
      if (!product.product_id) {
        console.warn('[ProductDetailPanel] No product_id found:', product);
        return;
      }
      
      setLoadingImages(true);
      try {
        const baseUrl = import.meta.env.VITE_RCC_BASE || '';
        const orgId = getOrgId();
        const fixUrl = (url) => {
          if (url && (url.includes('127.0.0.1') || url.includes('localhost'))) {
            const match = url.match(/(?:127\.0\.0\.1:\d+|localhost:\d+)(\/.*)/);
            if (match) return match[1];
          }
          return url;
        };

        // Run the catalog cache, current product's media, AND every variant's
        // media as ONE parallel batch (previously these were N+2 sequential
        // awaited round-trips, one after another) - that serial chain was
        // slow enough that the panel visibly opened with its fallback image
        // first and then "swapped" to the fetched image moments later,
        // which is the flicker/refresh the user was seeing. Fetching them
        // all at once means the whole panel resolves in ~1 round-trip.
        const variants = product.variantData?.variants || [];
        const [catalogResult, currentResult, ...variantResults] = await Promise.all([
          fetch(`${baseUrl}/rcc-api/catalog/products?org_id=${orgId}`)
            .then(r => r.ok ? r.json() : [])
            .catch(err => { console.error('[ProductDetailPanel] Failed to fetch products cache:', err); return []; }),
          fetch(`${baseUrl}/rcc-api/products/${product.product_id}/media?org_id=${orgId}`)
            .then(r => r.ok ? r.json() : { media: [] })
            .catch(() => ({ media: [] })),
          ...variants.map(variant =>
            variant.product_id
              ? fetch(`${baseUrl}/rcc-api/products/${variant.product_id}/media?org_id=${orgId}`)
                  .then(r => r.ok ? r.json() : { media: [] })
                  .catch(err => { console.error('[ProductDetailPanel] Failed to fetch variant images:', err); return { media: [] }; })
              : Promise.resolve({ media: [] })
          ),
        ]);

        const productsCache = {};
        catalogResult.forEach(p => { productsCache[p.product_id] = p; });
        setProductsCache(productsCache);

        const allImages = [];

        // Current product's own image (first entry in the vertical strip)
        const currentMedia = (currentResult.media || []).map(media => ({ ...media, url: fixUrl(media.url) }));
        allImages.push(currentMedia.length > 0 ? {
          ...currentMedia[0],
          product_id: product.product_id,
          product_name: product.product_name || product.name,
          price: product.price,
          is_current: true,
          has_placeholder: false,
          attributes: product.variantData?.attributes || [],
          promotion: product.promotion || null,
          qty_on_hand: product.qty_on_hand
        } : {
          url: null,
          product_id: product.product_id,
          product_name: product.product_name || product.name,
          price: product.price,
          is_current: true,
          has_placeholder: true,
          attributes: product.variantData?.attributes || [],
          promotion: product.promotion || null,
          qty_on_hand: product.qty_on_hand
        });

        // Other variants, in the same order they were requested
        variants.forEach((variant, i) => {
          if (!variant.product_id) return;
          const variantMedia = (variantResults[i]?.media || []).map(media => ({ ...media, url: fixUrl(media.url) }));
          const variantPrice = productsCache[variant.product_id]?.price || product.price;
          allImages.push(variantMedia.length > 0 ? {
            ...variantMedia[0],
            product_id: variant.product_id,
            product_name: variant.product_name,
            price: variantPrice,
            is_current: false,
            attributes: variant.attributes,
            has_placeholder: false,
            promotion: variant.promotion || null,
            qty_on_hand: variant.qty_on_hand
          } : {
            url: null,
            product_id: variant.product_id,
            product_name: variant.product_name,
            price: variantPrice,
            is_current: false,
            attributes: variant.attributes,
            has_placeholder: true,
            promotion: variant.promotion || null,
            qty_on_hand: variant.qty_on_hand
          });
        });
        
        console.log('[ProductDetailPanel] 📸 Total images collected:', allImages.length);
        // Set the initial selection atomically WITH productImages, in this one
        // async pass - never in a separate reactive effect keyed on
        // productImages, which could otherwise re-fire after a user's first
        // click and silently revert their selection back to the original.
        const initialIndex = allImages.findIndex(img => img.is_current);
        setSelectedImageIndex(initialIndex >= 0 ? initialIndex : 0);
        setProductImages(allImages);
      } catch (err) {
        console.error('[ProductDetailPanel] Failed to fetch product images:', err);
      } finally {
        setLoadingImages(false);
      }
    };
    
    fetchProductImages();
  }, [product.product_id, product.variantData]);

  // Organize attributes by type (image-based vs text-based)
  const { imageAttributes, textAttributes, allAttributes } = React.useMemo(() => {
    console.log("[ProductDetailPanel] FULL PRODUCT:", product);
    console.log("[ProductDetailPanel] Product attributes (old):", product.attributes);
    console.log("[ProductDetailPanel] Product variantData:", product.variantData);
    
    let attributesToProcess = product.attributes || {};
    
    // PRIORITY: Use variant group attributes if available
    if (product.variantData && product.variantData.attributes && product.variantData.attributes.length > 0) {
      console.log("[ProductDetailPanel] ✨ Using variant group attributes!");
      
      // Store old attributes for image/price lookup
      const oldAttrs = product.attributes || {};
      
      // Convert variant attributes to expected format
      // From: [{ attribute_name: "Flavour", attribute_value: "Raspberry" }]
      // To: { Flavour: [{ value: "Raspberry", price: null, image_url: null }] }
      const variantAttrs = {};
      product.variantData.attributes.forEach(attr => {
        const attrName = attr.attribute_name;
        const attrValue = attr.attribute_value;
        
        if (!variantAttrs[attrName]) {
          variantAttrs[attrName] = [];
        }
        
        // Try to find matching image/price from old attributes
        let imageUrl = null;
        let price = null;
        
        if (oldAttrs[attrName] && Array.isArray(oldAttrs[attrName])) {
          const match = oldAttrs[attrName].find(v => 
            v.value && attrValue && v.value.trim().toLowerCase() === attrValue.trim().toLowerCase()
          );
          if (match) {
            imageUrl = match.image_url || null;
            price = match.price || null;
            console.log(`[ProductDetailPanel] 🖼️ Found image for ${attrName}: ${attrValue}`, imageUrl);
          }
        }
        
        variantAttrs[attrName].push({
          value: attrValue,
          price: price,
          image_url: imageUrl
        });
      });
      
      // Get other products in the same variant group as additional options
      if (product.variantData.variants && product.variantData.variants.length > 0) {
        console.log("[ProductDetailPanel] 🔗 Found", product.variantData.variants.length, "other variants");
        product.variantData.variants.forEach(variant => {
          if (variant.attributes && variant.attributes.length > 0) {
            variant.attributes.forEach(attr => {
              const attrName = attr.attribute_name;
              const attrValue = attr.attribute_value;
              
              if (!variantAttrs[attrName]) {
                variantAttrs[attrName] = [];
              }
              
              // Only add if not already present
              if (!variantAttrs[attrName].some(v => v.value === attrValue)) {
                // Try to find matching image/price from old attributes
                let imageUrl = null;
                let price = null;
                
                if (oldAttrs[attrName] && Array.isArray(oldAttrs[attrName])) {
                  const match = oldAttrs[attrName].find(v => 
                    v.value && attrValue && v.value.trim().toLowerCase() === attrValue.trim().toLowerCase()
                  );
                  if (match) {
                    imageUrl = match.image_url || null;
                    price = match.price || null;
                  }
                }
                
                variantAttrs[attrName].push({
                  value: attrValue,
                  price: price,
                  image_url: imageUrl
                });
              }
            });
          }
        });
      }
      
      console.log("[ProductDetailPanel] 📦 Converted variant attributes with images:", variantAttrs);
      attributesToProcess = variantAttrs;
    }
    
    if (!attributesToProcess || Object.keys(attributesToProcess).length === 0) {
      console.warn("[ProductDetailPanel] ⚠️ No attributes found on product!");
      return { imageAttributes: {}, textAttributes: {}, allAttributes: {} };
    }
    
    const imageAttrs = {}; // Attributes that should show in vertical image strip (with placeholders if no images)
    const textAttrs = {};  // Attributes shown only as wheel pickers
    const allAttrs = {};   // All attributes organized by code
    
    // Attributes that should show in vertical strip (even without images)
    const visualAttributes = ['Flavour', 'Flavor', 'Color', 'Colour', 'Style', 'Variant', 'Type'];
    
    for (const [code, data] of Object.entries(attributesToProcess)) {
      const values = Array.isArray(data) ? data : [data];
      
      // Organize all values
      allAttrs[code] = values.filter(v => v.value && v.value.trim() !== '');
      
      // Show in vertical strip if: has images OR is a visual attribute (like Flavour)
      const hasImages = values.some(v => v.image_url);
      const isVisualAttribute = visualAttributes.includes(code);
      
      if (hasImages || isVisualAttribute) {
        imageAttrs[code] = allAttrs[code];
        console.log(`[ProductDetailPanel] ✅ Added "${code}" to imageAttributes (hasImages: ${hasImages}, isVisual: ${isVisualAttribute})`);
      } else {
        textAttrs[code] = allAttrs[code];
      }
    }
    
    console.log("[ProductDetailPanel] FINAL imageAttributes:", imageAttrs);
    console.log("[ProductDetailPanel] FINAL textAttributes:", textAttrs);
    return { imageAttributes: imageAttrs, textAttributes: textAttrs, allAttributes: allAttrs };
  }, [product.attributes, product.variantData]);

  // Initialize selections for NON-variant products only (old per-attribute
  // system). Variant-group products get their initial selectedImageIndex set
  // atomically inside the image-fetch effect above, and their displayed
  // selection is DERIVED from it via variantSelections below - there is no
  // separate reactive re-initializer for the variant case, which is what
  // previously raced against a user's first click.
  useEffect(() => {
    if (product.variantData) return;
    if (Object.keys(allAttributes).length === 0) {
      console.log('[ProductDetailPanel] ⚠️ No attributes available yet');
      return;
    }
    const initialSelections = {};
    Object.keys(allAttributes).forEach(code => {
      if (allAttributes[code].length > 0) {
        initialSelections[code] = allAttributes[code][0].value;
      }
    });
    console.log('[ProductDetailPanel] 🎬 Non-variant initial selections:', initialSelections);
    setSelections(initialSelections);
    setQuantity(1);
  }, [product.product_id, allAttributes, product.variantData]);

  // The ONE authoritative product identity for the currently selected variant
  // (or the original product if this isn't a variant-group product, or the
  // selection still points at the original/is_current entry).
  const effectiveProduct = React.useMemo(() => {
    if (!product.variantData || productImages.length === 0) return product;
    const variant = productImages[selectedImageIndex];
    if (!variant || variant.is_current) return product;
    return resolveEffectiveProduct(variant);
  }, [product, productImages, selectedImageIndex, resolveEffectiveProduct]);

  // The wheel picker's displayed value for variant-group products, derived
  // from the SAME selectedImageIndex - never a second source of truth.
  const variantSelections = React.useMemo(() => {
    if (!product.variantData) return null;
    const variant = productImages[selectedImageIndex];
    if (!variant?.attributes) return {};
    const s = {};
    variant.attributes.forEach(attr => { s[attr.attribute_name] = attr.attribute_value; });
    return s;
  }, [product.variantData, productImages, selectedImageIndex]);

  const displaySelections = product.variantData ? (variantSelections || {}) : selections;

  // Load the main image gallery for whichever product is CURRENTLY selected
  // (the original product initially, or a sibling variant after selection).
  // This is the single place currentProductMedia is ever set for variant-group
  // products - the thumbnail click and wheel picker both just move
  // selectedImageIndex, and this effect reacts to it uniformly.
  useEffect(() => {
    if (productImages.length === 0) return;
    const variant = productImages[selectedImageIndex];
    if (!variant?.product_id) return;
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = import.meta.env.VITE_RCC_BASE || '';
        const orgId = getOrgId();
        const res = await fetch(`${baseUrl}/rcc-api/products/${variant.product_id}/media?org_id=${orgId}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const fixedMedia = (data.media || []).map(media => {
          let url = media.url;
          if (url && (url.includes('127.0.0.1') || url.includes('localhost'))) {
            const match = url.match(/(?:127\.0\.0\.1:\d+|localhost:\d+)(\/.*)/);
            if (match) url = match[1];
          }
          return { ...media, url };
        });
        if (!cancelled) {
          setCurrentProductMedia(fixedMedia);
          setSelectedMediaIndex(0);
        }
      } catch (err) {
        console.error('[ProductDetailPanel] Failed to load media for selected variant:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedImageIndex, productImages]);

  // Scroll the selected variant's thumbnail to the centre (revolver style -
  // clicking an image brings the next one up/down to the middle).
  useEffect(() => {
    if (!product.variantData) return;
    const container = imageStripContainerRef.current;
    if (!container) return;
    const items = container.querySelectorAll('.attribute-image-item');
    if (items[selectedImageIndex]) {
      items[selectedImageIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedImageIndex, product.variantData]);

  // Scroll the selected horizontal thumbnail to the centre (same revolver
  // behaviour as the vertical strip above).
  useEffect(() => {
    const container = thumbnailsContainerRef.current;
    if (!container) return;
    const items = container.querySelectorAll('.thumbnail-item');
    if (items[selectedMediaIndex]) {
      items[selectedMediaIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [selectedMediaIndex]);

  // Calculate display price and image from selected attribute with price/image
  const { displayPrice, displayImage } = React.useMemo(() => {
    console.log('[ProductDetailPanel] 🔄 Recalculating displayImage...');
    console.log('[ProductDetailPanel] selectedImageIndex:', selectedImageIndex);
    console.log('[ProductDetailPanel] selectedMediaIndex:', selectedMediaIndex);
    console.log('[ProductDetailPanel] productImages.length:', productImages.length);
    console.log('[ProductDetailPanel] currentProductMedia.length:', currentProductMedia.length);
    console.log('[ProductDetailPanel] product.variantData:', !!product.variantData);
    
    // Log condition details
    if (productImages.length > 0) {
      console.log('[ProductDetailPanel] 📸 Available images:', productImages.map((img, i) => ({
        index: i,
        selected: i === selectedImageIndex,
        url: img.url ? img.url.substring(img.url.lastIndexOf('/') + 1) : 'PLACEHOLDER',
        product: img.product_name,
        price: img.price
      })));
    }
    
    console.log('[ProductDetailPanel] Condition check:', {
      hasImages: productImages.length > 0,
      hasVariantData: !!product.variantData,
      indexValid: selectedImageIndex < productImages.length,
      currentMediaCount: currentProductMedia.length,
      selectedMediaIndex: selectedMediaIndex
    });
    
    // PRIORITY 1: Use selected thumbnail from gallery (highest priority - user clicked a thumbnail)
    if (currentProductMedia.length > 0 && selectedMediaIndex < currentProductMedia.length) {
      const selectedMedia = currentProductMedia[selectedMediaIndex];
      console.log('[ProductDetailPanel] ✅ PRIORITY 1: Using thumbnail gallery image at index', selectedMediaIndex);
      console.log('[ProductDetailPanel] 🖼️ Image URL:', selectedMedia.url);
      
      // For variant products, try to get price from the vertical strip selection
      let priceToUse = effectiveProduct.price;
      if (product.variantData && productImages.length > 0 && selectedImageIndex < productImages.length) {
        priceToUse = productImages[selectedImageIndex].price || effectiveProduct.price;
      }
      
      return {
        displayPrice: priceToUse,
        displayImage: selectedMedia.url || fixImageUrl(effectiveProduct.image_url)
      };
    }
    
    // PRIORITY 2: Use selected variant from vertical strip
    if (productImages.length > 0 && product.variantData && selectedImageIndex < productImages.length) {
      const selectedThumbnail = productImages[selectedImageIndex];
      console.log('[ProductDetailPanel] ✅ PRIORITY 2: Using vertical strip variant at index', selectedImageIndex);
      console.log('[ProductDetailPanel] 💰 Price:', selectedThumbnail.price);
      console.log('[ProductDetailPanel] 🖼️ Image URL:', selectedThumbnail.url);
      return {
        displayPrice: selectedThumbnail.price || effectiveProduct.price,
        displayImage: selectedThumbnail.url || fixImageUrl(effectiveProduct.image_url)
      };
    }
    
    // PRIORITY 2: Find first image-based attribute with a price (old attribute system)
    for (const code of Object.keys(imageAttributes)) {
      const selectedValue = displaySelections[code];
      const selectedAttr = allAttributes[code]?.find(v => v.value === selectedValue);
      if (selectedAttr) {
        console.log('[ProductDetailPanel] 📷 Using image from attribute:', code, selectedValue);
        return {
          displayPrice: selectedAttr.price ?? effectiveProduct.price,
          displayImage: fixImageUrl(selectedAttr.image_url) || fixImageUrl(effectiveProduct.image_url)
        };
      }
    }
    
    // FALLBACK: Use product defaults
    console.log('[ProductDetailPanel] 📦 Using default product image');
    return {
      displayPrice: effectiveProduct.price,
      displayImage: fixImageUrl(effectiveProduct.image_url)
    };
  }, [displaySelections, imageAttributes, allAttributes, product, effectiveProduct, productImages, selectedImageIndex, product.variantData, currentProductMedia, selectedMediaIndex, fixImageUrl]);

  // Presentation-only fallback: never persisted/deleted/reordered, and never
  // written back into currentProductMedia - local media stays the sole source
  // of truth for any local-media operation. Only fills the carousel when
  // there is genuinely no local media, so a central/canonical image isn't
  // shown as a placeholder when the card/kiosk already display it.
  const carouselImages = React.useMemo(() => {
    if (currentProductMedia.length > 0) return currentProductMedia;
    if (displayImage) {
      return [{ url: displayImage, alt_text: 'Central Catalogue Image', is_central_display_only: true }];
    }
    return [];
  }, [currentProductMedia, displayImage]);

  // Promotion/stock for the currently selected variant - read directly from
  // effectiveProduct (the ONE authoritative identity) rather than re-deriving
  // a separate match against `selections`.
  const currentVariantPromotion = effectiveProduct.promotion || null;
  const currentVariantQtyOnHand = effectiveProduct.qty_on_hand;

  // Determine if promotion should be shown (based on currently selected variant)
  const shouldShowPromotion = React.useMemo(() => {
    return !!currentVariantPromotion;
  }, [currentVariantPromotion]);

  const showPlaceholder = !displayImage || imageError;

  // ESC key to close zoom modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showZoom) {
        setShowZoom(false);
      }
    };
    if (showZoom) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [showZoom]);

  // Wheel picker for all screens (touch-friendly interface)
  // No need for resize detection - always use wheel picker

  // Stock availability logic (use current variant's stock, not original product)
  const showStockAvailability = catalogSettings.show_stock_availability !== false;
  const lowStockThreshold = catalogSettings.low_stock_threshold || 10;
  const qtyOnHand = currentVariantQtyOnHand; // Use current variant's stock
  const isOutOfStock = qtyOnHand !== null && qtyOnHand !== undefined && qtyOnHand < 0;

  const handleAddToBasket = () => {
    if (isOutOfStock) {
      return; // Don't add out-of-stock items
    }
    setAdding(true);
    
    // Build combined attribute selections for basket
    const selectedAttributes = Object.entries(displaySelections).map(([code, value]) => {
      const selectedAttr = allAttributes[code]?.find(v => v.value === value);
      return {
        code,
        value,
        price: selectedAttr?.price,
        image_url: selectedAttr?.image_url
      };
    }).filter(attr => attr.value);
    
    // Build display name with all attribute selections
    const baseName = effectiveProduct.product_name || effectiveProduct.name;
    const attributeParts = selectedAttributes.map(attr => attr.value).join(' - ');
    const displayName = attributeParts ? `${baseName} - ${attributeParts}` : baseName;
    
    // Use price from image attribute (or default)
    // Use the promotion for the CURRENTLY SELECTED VARIANT, not the original product
    const itemToAdd = {
      ...effectiveProduct,
      product_name: displayName,
      price: displayPrice,
      selectedAttributes: selectedAttributes,
      promotion: currentVariantPromotion || null // Override with current variant's promotion
    };
    
    addItem(itemToAdd, quantity);
    setTimeout(() => {
      setAdding(false);
      setQuantity(1);
    }, 500);
  };

  const incrementQty = () => setQuantity(prev => Math.min(prev + 1, 99));
  const decrementQty = () => setQuantity(prev => Math.max(prev - 1, 1));

  // Get first image-based attribute for image strip
  const primaryImageAttr = Object.keys(imageAttributes)[0];

  // Handler for attribute selection
  // Scroll selected image to center (revolver behavior)
  const scrollToCenter = (index) => {
    if (!imageStripContainerRef.current) {
      console.error(`[Scroll] Container ref is null!`);
      return;
    }
    
    const container = imageStripContainerRef.current;
    const imageItems = Array.from(container.children).filter(child => 
      child.classList && child.classList.contains('attribute-image-item')
    );
    
    if (!imageItems[index]) {
      console.error(`[Scroll] Item ${index} not found (have ${imageItems.length} items)`);
      return;
    }
    
    const item = imageItems[index];
    // Fixed dimensions for smaller panel: container 160px, padding 55px top/bottom
    const PADDING = 55; // Top padding that centers first item
    const GAP = 12; // Gap between images
    const ITEM_HEIGHT = 48; // Fixed item height
    
    // Calculate scroll position: each item is ITEM_HEIGHT + GAP from previous
    const scrollTo = index * (ITEM_HEIGHT + GAP);
    
    console.log(`[Scroll] → index ${index}, scrollTo: ${scrollTo}px`);
    
    container.scrollTo({
      top: scrollTo,
      behavior: 'smooth'
    });
  };

  const handleSelectAttribute = (code, value) => {
    console.log(`[ProductDetailPanel] 🎯 handleSelectAttribute: ${code} = "${value}"`);
    
    if (!product.variantData) {
      // Old behavior for non-variant products
      setSelections(prev => ({ ...prev, [code]: value }));
      if (code === primaryImageAttr) {
        const selectedIndex = imageAttributes[primaryImageAttr]?.findIndex(attr => attr.value === value);
        if (selectedIndex >= 0) setTimeout(() => scrollToCenter(selectedIndex), 50);
      }
      return;
    }
    
    // For variant products: find the sibling whose attributes include this
    // value, and make it the ONE authoritative selection - everything else
    // (title, barcode, price, stock, image, thumbnail highlight) derives
    // from this single index via the memos/effects above.
    const matchIndex = productImages.findIndex(img =>
      img.attributes?.some(a => a.attribute_name === code && a.attribute_value === value)
    );
    
    if (matchIndex === -1) {
      console.error('[ProductDetailPanel] ❌ No variant found for', code, '=', value);
      return;
    }
    
    console.log('[ProductDetailPanel] ✅ Found variant:', productImages[matchIndex].product_name);
    setSelectedImageIndex(matchIndex);
  };

  // Scroll to center when selection changes (initial load or external updates)
  useEffect(() => {
    if (!primaryImageAttr || !selections[primaryImageAttr]) return;
    
    const selectedIndex = imageAttributes[primaryImageAttr]?.findIndex(
      attr => attr.value === selections[primaryImageAttr]
    );
    
    if (selectedIndex >= 0 && imageStripContainerRef.current) {
      const timeoutId = setTimeout(() => {
        console.log(`[Init] Scrolling to "${selections[primaryImageAttr]}"`);
        scrollToCenter(selectedIndex);
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [selections, primaryImageAttr]);

  // Listen for scroll events (touch screen swipes) and update selection to centered item
  useEffect(() => {
    if (!primaryImageAttr || !imageStripContainerRef.current) return;
    
    const container = imageStripContainerRef.current;
    let scrollTimeout;
    
    const handleScroll = () => {
      // Debounce: wait until scrolling stops
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollTop = container.scrollTop;
        const ITEM_HEIGHT = 48;
        const GAP = 12;
        const ITEM_SPACING = ITEM_HEIGHT + GAP; // 60px total per item
        
        // Calculate which item is closest to center based on scroll position
        const closestIndex = Math.round(scrollTop / ITEM_SPACING);
        
        // Update selection to the centered item
        const centeredAttr = imageAttributes[primaryImageAttr]?.[closestIndex];
        if (centeredAttr && selections[primaryImageAttr] !== centeredAttr.value) {
          console.log(`[Scroll Detection] Centered on index ${closestIndex}: "${centeredAttr.value}"`);
          setSelections(prev => ({ ...prev, [primaryImageAttr]: centeredAttr.value }));
        }
      }, 100); // Wait 100ms after scroll stops
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [primaryImageAttr, imageAttributes, selections]);

  return (
    <>
      <div className="product-detail-panel">
        <div className={`product-detail-content-new ${descriptionCollapsed ? 'description-collapsed' : ''}`}>
          {/* Close Button (fixed, overlays the grid) */}
          <button className="product-detail-close" onClick={onClose} title="Close">
            ✕
          </button>

          {/* DIVIDER 1: Description region - bounded, never expands past its own column */}
          <div className="pdp-region-description">
            <div className={`product-description-section ${descriptionCollapsed ? 'collapsed' : 'expanded'}`}>
              {!descriptionCollapsed && (
                <div className="description-scroll-container">
                  {product.description ? (
                    <>
                      <div className="description-header">Description</div>
                      <div className="description-text">{product.description}</div>
                    </>
                  ) : (
                    <div className="description-empty">No description available</div>
                  )}
                </div>
              )}
              <button 
                className="description-toggle-btn" 
                onClick={() => setDescriptionCollapsed(!descriptionCollapsed)}
                title={descriptionCollapsed ? "Show Description" : "Hide Description"}
              >
                {descriptionCollapsed ? '▶' : '◀'}
              </button>
            </div>
          </div>

          {/* DIVIDER 2: Image gallery region - nested grid (2-1 strip | 2-3 image + 2-2 horizontal strip) */}
          <div className="pdp-region-gallery">
            {/* 2-1: Product Images Vertical Strip (prioritize for variant group products) */}
            {productImages.length > 0 && product.variantData && (
              <div className="attribute-strip">
                <div className="attribute-strip-header">
                  {product.variantData.groupName || 'Variants'}
                </div>
                <div className="attribute-images-container" ref={imageStripContainerRef}>
                  {productImages.map((media, idx) => (
                    <div
                      key={`product-image-${idx}`}
                      className={`attribute-image-item ${idx === selectedImageIndex ? 'selected' : ''}`}
                      onClick={() => {
                        console.log('[ProductDetailPanel] 🖼️ Clicked thumbnail', idx, media);
                        // ONE authoritative selection - effectiveProduct, the
                        // wheel picker's displayed value, and the main image
                        // gallery all derive from this index automatically.
                        setSelectedImageIndex(idx);
                      }}
                    >
                      {media.url ? (
                        <img 
                          src={media.url} 
                          alt={`Product image ${idx + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#8B7355',
                          fontSize: '32px',
                          color: '#fff'
                        }}>
                          📦
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 2-1: Attribute Images Vertical Strip (only for non-variant products with attribute images) */}
            {!product.variantData && primaryImageAttr && imageAttributes[primaryImageAttr]?.length > 0 && (
              <div className="attribute-strip">
                <div className="attribute-strip-header">{primaryImageAttr}</div>
                <div className="attribute-images-container" ref={imageStripContainerRef}>
                  {imageAttributes[primaryImageAttr].map((attr, idx) => (
                    <div
                      key={`${primaryImageAttr}-${idx}`}
                      className={`attribute-image-item ${selections[primaryImageAttr] === attr.value ? 'selected' : ''}`}
                      onClick={() => handleSelectAttribute(primaryImageAttr, attr.value)}
                    >
                      {attr.image_url ? (
                        <img src={fixImageUrl(attr.image_url)} alt={`${primaryImageAttr}: ${attr.value}`} />
                      ) : (
                        <div style={{
                          width: '100%',
                          aspectRatio: '1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f3f4f6',
                          fontSize: '32px',
                          color: '#cbd5e1'
                        }}>
                          📦
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 2-3 + 2-2: Large image (centered) with horizontal thumbnail strip below */}
            <div className="pdp-gallery-main">
              <div className="product-image-container" style={{ position: 'relative' }}>
                <ImageCarousel
                  images={carouselImages}
                  currentIndex={selectedMediaIndex}
                  onIndexChange={setSelectedMediaIndex}
                  showArrows={carouselImages.length > 1}
                  showDots={false}
                  className="product-detail-main-image"
                />

                {/* Zoom Icon - Only show if there's an actual image */}
                {!showPlaceholder && carouselImages.length > 0 && carouselImages[selectedMediaIndex]?.url && (
                  <button className="image-zoom-icon" onClick={() => setShowZoom(true)} title="Zoom image">
                    🔍
                  </button>
                )}
              </div>

              {/* 2-2: Thumbnail Gallery - Local media only; a synthetic central entry is never multiple */}
              {currentProductMedia.length > 1 && (
                <div className="product-thumbnails-gallery" ref={thumbnailsContainerRef}>
                  {currentProductMedia.map((media, idx) => (
                    <div
                      key={`thumb-${idx}`}
                      className={`thumbnail-item ${idx === selectedMediaIndex ? 'active' : ''}`}
                      onClick={() => setSelectedMediaIndex(idx)}
                    >
                      {media.url ? (
                        <img 
                          src={media.url} 
                          alt={media.alt_text || `Image ${idx + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#e5e7eb',
                          fontSize: '16px'
                        }}>📦</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* DIVIDER 3: Product info column - wraps the text block (title/price/
              location/promo) and the side panel (stock/wheel/actions) so
              both render exactly as before at every size except 480px,
              where the wrapper is unwrapped (display:contents) and the two
              pieces become independent grid items placed by the outer grid. */}
          <div className="pdp-info-column">
          <div className="pdp-region-info">
            <div className="pdp-title-block">
              <h2 className="product-detail-name">{effectiveProduct.product_name || effectiveProduct.name}</h2>

              {/* Price + Barcode - barcode sits right-aligned next to Price with room between them */}
              <div className="pdp-price-row">
                <div className="product-detail-price">
                  £{Number(displayPrice).toFixed(2)}
                </div>
                {kioskMode && effectiveProduct.barcode && (
                  <div className="pdp-barcode-wrap">
                    <BarcodeDisplay 
                      key={effectiveProduct.barcode || effectiveProduct.product_id} 
                      barcode={effectiveProduct.barcode} 
                      width={0.75} 
                      height={8} 
                      fontSize={10} 
                    />
                  </div>
                )}
              </div>

              {/* Location Watermark (KIOSK MODE ONLY) */}
              {kioskMode && product.bin_location && (
                <div className="pdp-location-label" style={{
                  fontSize: '11px',
                  color: 'var(--color-text-secondary)',
                  fontWeight: 500,
                  fontFamily: 'monospace',
                  opacity: 0.7,
                  letterSpacing: '0.5px'
                }}>
                  loc: {product.bin_location}
                </div>
              )}
            </div>

            {/* Promotion Label - Shows for currently selected variant */}
            {shouldShowPromotion && (
              <div
                className="pdp-promotion-label"
                style={{
                  background: 'linear-gradient(135deg, #fb923c, #f43f5e)',
                  color: 'white',
                  boxShadow: '0 2px 6px rgba(244, 63, 94, 0.35)',
                }}
              >
                {typeof currentVariantPromotion === 'string' ? currentVariantPromotion : currentVariantPromotion.text}
              </div>
            )}
          </div>

          {/* Side panel (only relevant at narrow sizes where it renders next
              to the main image - Stock, Wheel Picker(s), Qty, Add to Basket) */}
          <div className="pdp-side-panel">
            {/* Stock Availability Label */}
            {showStockAvailability && qtyOnHand !== null && qtyOnHand !== undefined && (
              <div
                style={{
                  color: isOutOfStock ? '#ef4444' : (qtyOnHand <= lowStockThreshold ? '#f59e0b' : '#10b981'),
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '4px 8px',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                }}
              >
                {isOutOfStock ? 'Out of Stock' : (qtyOnHand <= lowStockThreshold ? `Only Left: ${qtyOnHand}` : `In Stock: ${qtyOnHand}`)}
              </div>
            )}

            {/* ALL Attributes as Selectable Lists */}
            {Object.keys(allAttributes).map(code => {
              const attrValues = allAttributes[code];
                
              if (!attrValues || attrValues.length === 0) {
                console.warn('[ProductDetailPanel] ⚠️ No values for attribute:', code);
                return null;
              }

              return (
                <div key={code} className="attribute-selector-group">
                  {/* iOS-style Wheel Picker for all screens (touch-friendly) */}
                  <WheelPicker
                    label={code}
                    options={attrValues.map(attr => attr.value)}
                    value={displaySelections[code] || ''}
                    onChange={(value) => handleSelectAttribute(code, value)}
                  />
                </div>
              );
            })}

            {/* DIVIDER 3-1: Purchase actions (Quantity + Add to Basket), bounded within Divider 3 */}
            <div className="pdp-region-actions">
              <div className="product-detail-quantity">
                <button
                  className="qty-btn"
                  onClick={decrementQty}
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="qty-value">{quantity}</span>
                <button
                  className="qty-btn"
                  onClick={incrementQty}
                  disabled={quantity >= 99}
                >
                  +
                </button>
              </div>

              <button
                className={`add-to-basket-btn ${adding ? 'adding' : ''} ${isOutOfStock ? 'out-of-stock' : ''}`}
                onClick={handleAddToBasket}
                disabled={adding || isOutOfStock}
                style={{
                  opacity: isOutOfStock ? 0.5 : 1,
                  cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                  backgroundColor: isOutOfStock ? '#9ca3af' : undefined
                }}
              >
                {isOutOfStock ? '✗ Out of Stock' : (adding ? 'Added!' : 'Add to Basket')}
              </button>
            </div>
          </div>
          </div>

          {/* RIGHT: Reserved empty space so the existing floating basket control is never covered */}
          <div className="pdp-region-reserved" aria-hidden="true" />
        </div>
      </div>

      {/* Zoom Modal */}
      {showZoom && (
        <div className="zoom-modal" onClick={() => setShowZoom(false)}>
          <div className="zoom-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="zoom-close-btn" onClick={() => setShowZoom(false)}>
              ✕
            </button>
            <img
              src={displayImage}
              alt={effectiveProduct.product_name || effectiveProduct.name}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </div>
        </div>
      )}
    </>
  );
}
